import { useState } from 'react';
import { useForm } from 'react-hook-form';
import type { SimilarMatch, Tenant } from '../types';
import type { TenantSaveResult } from '../api';

type Props = {
  initial?: Tenant | null;
  save: (input: Partial<Tenant>, force: boolean) => Promise<TenantSaveResult>;
  onSuccess: () => void;
  onCancel: () => void;
  submitLabel: string;
};

type FormShape = {
  companyName: string;
  tradingName: string;
  companyRegistration: string;
  tpin: string;
  contactPerson: string;
  contactEmail: string;
  contactNumber: string;
  invoiceRecipient: string;
};

export const TenantForm = ({ initial, save, onSuccess, onCancel, submitLabel }: Props) => {
  const { register, handleSubmit, formState } = useForm<FormShape>({
    defaultValues: {
      companyName: initial?.companyName ?? '',
      tradingName: initial?.tradingName ?? '',
      companyRegistration: initial?.companyRegistration ?? '',
      tpin: initial?.tpin ?? '',
      contactPerson: initial?.contactPerson ?? '',
      contactEmail: initial?.contactEmail ?? '',
      contactNumber: initial?.contactNumber ?? '',
      invoiceRecipient: initial?.invoiceRecipient ?? '',
    },
  });
  const [similar, setSimilar] = useState<SimilarMatch[]>([]);
  const [dupTpin, setDupTpin] = useState<string | null>(null);

  const onValid = async (data: FormShape, force: boolean) => {
    const result = await save(
      {
        companyName: data.companyName,
        tradingName: data.tradingName || null,
        companyRegistration: data.companyRegistration || null,
        tpin: data.tpin || null,
        contactPerson: data.contactPerson || null,
        contactEmail: data.contactEmail || null,
        contactNumber: data.contactNumber || null,
        invoiceRecipient: data.invoiceRecipient || null,
      },
      force,
    );
    if (result.ok) {
      onSuccess();
      return;
    }
    if (result.reason === 'duplicate_tpin') {
      setDupTpin(result.tpin);
      setSimilar([]);
      return;
    }
    setDupTpin(null);
    setSimilar(result.similar);
  };

  return (
    <form
      onSubmit={(e) => {
        void handleSubmit((d) => onValid(d, false))(e);
      }}
    >
      <div className="form-grid">
        <div className="field full">
          <label>Company name *</label>
          <input {...register('companyName', { required: true, onChange: () => setSimilar([]) })} />
        </div>
        <div className="field">
          <label>Trading name</label>
          <input {...register('tradingName')} />
        </div>
        <div className="field">
          <label>Company registration</label>
          <input {...register('companyRegistration')} />
        </div>
        <div className="field">
          <label>TPIN</label>
          <input {...register('tpin', { onChange: () => setDupTpin(null) })} />
        </div>
        <div className="field">
          <label>Contact person</label>
          <input {...register('contactPerson')} />
        </div>
        <div className="field">
          <label>Contact email</label>
          <input {...register('contactEmail')} />
        </div>
        <div className="field">
          <label>Contact number</label>
          <input {...register('contactNumber')} />
        </div>
        <div className="field full">
          <label>Invoice recipient</label>
          <input {...register('invoiceRecipient')} />
        </div>
      </div>
      <p className="muted" style={{ marginTop: 8, fontSize: 13 }}>
        Sage codes are set per lease (Leases → Edit) — a tenant can hold different Sage accounts
        for different shops.
      </p>
      {dupTpin && (
        <div
          style={{
            marginTop: 12,
            padding: 12,
            border: '1px solid #c0392b',
            background: '#fdecea',
            borderRadius: 6,
          }}
        >
          <strong>TPIN {dupTpin} already belongs to another tenant.</strong>
          <div style={{ marginTop: 8, fontSize: 13, color: '#7a241c' }}>
            Each TPIN must be unique. Clear or correct the TPIN, or merge with the existing tenant.
          </div>
        </div>
      )}
      {similar.length > 0 && (
        <div
          style={{
            marginTop: 12,
            padding: 12,
            border: '1px solid #d9a300',
            background: '#fff8e6',
            borderRadius: 6,
          }}
        >
          <strong>Similar tenants already exist:</strong>
          <ul style={{ margin: '8px 0 0', paddingLeft: 20 }}>
            {similar.map((s) => (
              <li key={s.id}>
                {s.name} ({Math.round(s.score * 100)}% match)
              </li>
            ))}
          </ul>
          <div style={{ marginTop: 8, fontSize: 13, color: '#665100' }}>
            Edit the name to avoid a duplicate, or save anyway.
          </div>
        </div>
      )}
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
        <button type="button" onClick={onCancel}>
          Cancel
        </button>
        {similar.length > 0 && (
          <button
            type="button"
            disabled={formState.isSubmitting}
            onClick={(e) => {
              void handleSubmit((d) => onValid(d, true))(e);
            }}
          >
            Save anyway
          </button>
        )}
        <button type="submit" className="primary" disabled={formState.isSubmitting}>
          {submitLabel}
        </button>
      </div>
    </form>
  );
};
